import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Admin.css';
import { PackageIcon, UsersIcon, TruckIcon } from '../../components/SvgIcons';
import AedPrice from '../../components/AedPrice';

const EMIRATE_NAMES = {
  abu_dhabi: { en: 'Abu Dhabi', ar: 'أبوظبي' },
  al_ain: { en: 'Al Ain', ar: 'العين' },
  dubai: { en: 'Dubai', ar: 'دبي' },
  sharjah: { en: 'Sharjah', ar: 'الشارقة' },
  ajman: { en: 'Ajman', ar: 'عجمان' },
  umm_al_quwain: { en: 'Umm Al Quwain', ar: 'أم القيوين' },
  ras_al_khaimah: { en: 'Ras Al Khaimah', ar: 'رأس الخيمة' },
  fujairah: { en: 'Fujairah', ar: 'الفجيرة' }
};

const CITY_COLORS = {
  abu_dhabi:      { bg: '#e3f2fd', color: '#1565c0', gradient: 'linear-gradient(135deg, #1565c0, #42a5f5)' },
  dubai:          { bg: '#fce4ec', color: '#c62828', gradient: 'linear-gradient(135deg, #c62828, #ef5350)' },
  sharjah:        { bg: '#e8f5e9', color: '#2e7d32', gradient: 'linear-gradient(135deg, #2e7d32, #66bb6a)' },
  al_ain:         { bg: '#fff3e0', color: '#e65100', gradient: 'linear-gradient(135deg, #e65100, #ff9800)' },
  ajman:          { bg: '#f3e5f5', color: '#7b1fa2', gradient: 'linear-gradient(135deg, #7b1fa2, #ab47bc)' },
  ras_al_khaimah: { bg: '#e0f7fa', color: '#00695c', gradient: 'linear-gradient(135deg, #00695c, #26a69a)' },
  fujairah:       { bg: '#fbe9e7', color: '#bf360c', gradient: 'linear-gradient(135deg, #bf360c, #ff7043)' },
  umm_al_quwain:  { bg: '#e8eaf6', color: '#283593', gradient: 'linear-gradient(135deg, #283593, #5c6bc0)' }
};

const STATUS_CONFIG = {
  created:    { label_en: 'Created',    label_ar: 'تم الإنشاء',    color: '#6b7280', bg: '#f3f4f6',  border: '#e5e7eb' },
  pending:    { label_en: 'Pending',    label_ar: 'قيد الانتظار',  color: '#d97706', bg: '#fffbeb',  border: '#fde68a' },
  processing: { label_en: 'Processing', label_ar: 'قيد المعالجة', color: '#3478f6', bg: '#eff6ff',  border: '#bfdbfe' },
  shipped:    { label_en: 'Shipped',    label_ar: 'تم الشحن',      color: '#7c3aed', bg: '#f5f3ff',  border: '#ddd6fe' },
  delivered:  { label_en: 'Delivered',  label_ar: 'تم التوصيل',    color: '#059669', bg: '#ecfdf5',  border: '#a7f3d0' },
  cancelled:  { label_en: 'Cancelled',  label_ar: 'ملغي',          color: '#dc2626', bg: '#fef2f2',  border: '#fecaca' }
};

const StatusIcon = ({ status, color, size = 20 }) => {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (status) {
    case 'created': return <svg {...props}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
    case 'pending': return <svg {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    case 'processing': return <svg {...props}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;
    case 'shipped': return <svg {...props}><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>;
    case 'delivered': return <svg {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
    case 'cancelled': return <svg {...props}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
    default: return null;
  }
};

const Stats = () => {
  const { token } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [topCustomers, setTopCustomers] = useState([]);
  const [productStats, setProductStats] = useState([]);
  const [emirateStats, setEmirateStats] = useState([]);
  const [statusStats, setStatusStats] = useState([]);
  const [emirateStatusStats, setEmirateStatusStats] = useState({});
  const [paymentStats, setPaymentStats] = useState(null);
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [showCustomers, setShowCustomers] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/orders/stats/detailed', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTopCustomers(data.topCustomers || []);
        setProductStats(data.productStats || []);
        setEmirateStats(data.emirateStats || []);
        setStatusStats(data.statusStats || []);
        setEmirateStatusStats(data.emirateStatusStats || {});
        setPaymentStats(data.paymentStats || null);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Computed values
  const totalAllOrders = emirateStats.reduce((sum, em) => sum + em.orderCount, 0);
  const totalAllRevenue = statusStats.reduce((sum, s) => sum + s.revenue, 0);
  const activeStatusStats = selectedCity === 'all' ? statusStats : (emirateStatusStats[selectedCity] || []);
  const activeTotalOrders = activeStatusStats.reduce((sum, s) => sum + s.count, 0);
  const activeTotalRevenue = activeStatusStats.reduce((sum, s) => sum + s.revenue, 0);
  const orderedStatuses = ['created', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'];
  const statusData = orderedStatuses.map(s => {
    const found = activeStatusStats.find(ss => ss.status === s);
    return { status: s, count: found ? found.count : 0, revenue: found ? found.revenue : 0 };
  });

  const selectedCityName = selectedCity === 'all'
    ? (language === 'ar' ? 'الكل' : 'All')
    : (EMIRATE_NAMES[selectedCity] ? (language === 'ar' ? EMIRATE_NAMES[selectedCity].ar : EMIRATE_NAMES[selectedCity].en) : selectedCity);

  if (loading) {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="loading-box"><div className="spinner"></div></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : ''}`}>
      <div className="container">
        {/* Nav */}
        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '20px', ...(window.innerWidth >= 768 ? { position: 'sticky', top: '72px', zIndex: 99 } : {}) }}>
          <div className="admin-nav" style={{ marginBottom: 0 }}>
            <Link to="/dashboard4">{language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}</Link>
            <Link to="/dashboard4/products">{t('products')}</Link>
            <Link to="/dashboard4/orders">{t('orders')}</Link>
            <Link to="/dashboard4/create-order">{language === 'ar' ? 'إنشاء طلب' : 'Create Order'}</Link>
            <Link to="/dashboard4/inventory">{language === 'ar' ? 'المخزون' : 'Inventory'}</Link>
            <Link to="/dashboard4/stats" className="active">{language === 'ar' ? 'الإحصائيات' : 'Stats'}</Link>
            <Link to="/dashboard4/settings">{language === 'ar' ? 'الإعدادات' : 'Settings'}</Link>
          </div>
        </div>

        {/* ====== Hero Summary ====== */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
          gap: isMobile ? '10px' : '14px',
          marginBottom: '20px'
        }}>
          {/* Total Orders */}
          <div style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '20px', padding: isMobile ? '20px 16px' : '24px 20px',
            position: 'relative', overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', opacity: 0.07 }}>
              <svg width={isMobile ? 70 : 90} height={isMobile ? 70 : 90} viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="1.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg>
            </div>
            <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#94a3b8', marginBottom: '8px' }}>
              {language === 'ar' ? 'الطلبات' : 'Orders'}
            </div>
            <div style={{ fontSize: isMobile ? '40px' : '48px', fontWeight: '900', lineHeight: 1, letterSpacing: '-2px', color: '#1e293b' }}>
              {totalAllOrders}
            </div>
          </div>

          {/* Total Revenue */}
          <div style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '20px', padding: isMobile ? '20px 16px' : '24px 20px',
            position: 'relative', overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', opacity: 0.07 }}>
              <svg width={isMobile ? 70 : 90} height={isMobile ? 70 : 90} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </div>
            <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#94a3b8', marginBottom: '8px' }}>
              {language === 'ar' ? 'الإيرادات' : 'Revenue'}
            </div>
            <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '900', lineHeight: 1, letterSpacing: '-1px', color: '#059669' }}>
              <AedPrice amount={totalAllRevenue} />
            </div>
          </div>

          {/* Cities */}
          <div style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '20px', padding: isMobile ? '20px 16px' : '24px 20px',
            position: 'relative', overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', opacity: 0.07 }}>
              <svg width={isMobile ? 70 : 90} height={isMobile ? 70 : 90} viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            </div>
            <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#94a3b8', marginBottom: '8px' }}>
              {language === 'ar' ? 'المدن' : 'Cities'}
            </div>
            <div style={{ fontSize: isMobile ? '40px' : '48px', fontWeight: '900', lineHeight: 1, letterSpacing: '-2px', color: '#7c3aed' }}>
              {emirateStats.length}
            </div>
          </div>

          {/* Products Sold */}
          <div style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '20px', padding: isMobile ? '20px 16px' : '24px 20px',
            position: 'relative', overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', opacity: 0.07 }}>
              <svg width={isMobile ? 70 : 90} height={isMobile ? 70 : 90} viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.5"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
            </div>
            <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#94a3b8', marginBottom: '8px' }}>
              {language === 'ar' ? 'المنتجات' : 'Products'}
            </div>
            <div style={{ fontSize: isMobile ? '40px' : '48px', fontWeight: '900', lineHeight: 1, letterSpacing: '-2px', color: '#d97706' }}>
              {productStats.filter(p => Math.max(0, (p.baseStock || 0) - (p.currentStock || 0)) > 0).length}
            </div>
          </div>
        </div>

        {/* ====== Payment Summary ====== */}
        {paymentStats && paymentStats.paidCount > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            borderRadius: '20px',
            padding: isMobile ? '18px 16px' : '22px 28px',
            marginBottom: '20px',
            border: '1px solid #a7f3d0',
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? '12px' : '24px',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{
                width: isMobile ? '44px' : '52px', height: isMobile ? '44px' : '52px',
                borderRadius: '16px', background: '#059669',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <svg width={isMobile ? 22 : 26} height={isMobile ? 22 : 26} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <div>
                <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '700', color: '#065f46', marginBottom: '2px' }}>
                  {language === 'ar' ? 'المدفوعات المحصلة' : 'Payments Collected'}
                </div>
                <div style={{ fontSize: isMobile ? '11px' : '14px', color: '#047857' }}>
                  {paymentStats.paidCount} {language === 'ar' ? 'طلب مدفوع' : 'paid orders'} — {language === 'ar' ? 'تم التحويل للبنك' : 'transferred to bank'}
                </div>
              </div>
            </div>
            <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '900', color: '#059669', lineHeight: 1, letterSpacing: '-1px' }}>
                <AedPrice amount={paymentStats.totalPaid} />
              </div>
              {paymentStats.pendingCount > 0 && (
                <div style={{ fontSize: isMobile ? '11px' : '14px', color: '#d97706', fontWeight: '600', marginTop: '4px' }}>
                  {paymentStats.pendingCount} {language === 'ar' ? 'قيد الانتظار' : 'pending'} · <AedPrice amount={paymentStats.totalPending} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ====== City Filter — Horizontal Scroll on Mobile ====== */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px'
          }}>
            <TruckIcon size="16px" />
            <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#1e293b' }}>
              {language === 'ar' ? 'حسب الإمارة' : 'By Emirate'}
            </span>
          </div>

          <div style={{
            display: 'flex', gap: '8px', overflowX: 'auto',
            paddingBottom: '6px', WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none', msOverflowStyle: 'none'
          }}>
            {/* All pill */}
            <button
              onClick={() => setSelectedCity('all')}
              style={{
                flexShrink: 0,
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 16px', borderRadius: '14px',
                border: selectedCity === 'all' ? '2px solid #3478f6' : '1.5px solid #e5e7eb',
                background: selectedCity === 'all' ? '#3478f6' : 'white',
                color: selectedCity === 'all' ? 'white' : '#374151',
                cursor: 'pointer', transition: 'all 0.15s', fontSize: '14px', fontWeight: '700'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={selectedCity === 'all' ? 'white' : '#3478f6'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              <span>{language === 'ar' ? 'الكل' : 'All'}</span>
              <span style={{
                background: selectedCity === 'all' ? 'rgba(255,255,255,0.2)' : '#f1f5f9',
                padding: '2px 8px', borderRadius: '8px', fontSize: '14px', fontWeight: '800'
              }}>{totalAllOrders}</span>
            </button>

            {emirateStats.map((e) => {
              const emirateName = EMIRATE_NAMES[e.emirate];
              const displayName = emirateName ? (language === 'ar' ? emirateName.ar : emirateName.en) : e.emirate;
              const cityColor = CITY_COLORS[e.emirate] || { bg: '#f3f4f6', color: '#374151', gradient: 'linear-gradient(135deg, #374151, #6b7280)' };
              const isSelected = selectedCity === e.emirate;
              return (
                <button
                  key={e.emirate}
                  onClick={() => setSelectedCity(e.emirate)}
                  style={{
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '10px 16px', borderRadius: '14px',
                    border: isSelected ? `2px solid ${cityColor.color}` : '1.5px solid #e5e7eb',
                    background: isSelected ? cityColor.color : 'white',
                    color: isSelected ? 'white' : '#374151',
                    cursor: 'pointer', transition: 'all 0.15s', fontSize: '14px', fontWeight: '700'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isSelected ? 'white' : cityColor.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span>{displayName}</span>
                  <span style={{
                    background: isSelected ? 'rgba(255,255,255,0.25)' : cityColor.bg,
                    color: isSelected ? 'white' : cityColor.color,
                    padding: '2px 8px', borderRadius: '8px', fontSize: '14px', fontWeight: '800'
                  }}>{e.orderCount}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ====== Status Breakdown Grid ====== */}
        <div style={{
          background: 'white', borderRadius: '20px', border: '1px solid #e5e7eb',
          padding: isMobile ? '16px' : '24px', marginBottom: '20px'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: isMobile ? '16px' : '20px', flexWrap: 'wrap', gap: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#1e293b' }}>
                {language === 'ar' ? 'تفاصيل الحالات' : 'Status Breakdown'}
              </span>
              {selectedCity !== 'all' && (
                <span style={{
                  fontSize: isMobile ? '11px' : '14px', fontWeight: '700',
                  background: (CITY_COLORS[selectedCity] || {}).bg || '#f1f5f9',
                  color: (CITY_COLORS[selectedCity] || {}).color || '#374151',
                  padding: '4px 10px', borderRadius: '8px'
                }}>
                  {selectedCityName}
                </span>
              )}
            </div>
            <span style={{ fontSize: isMobile ? '12px' : '14px', color: '#64748b', fontWeight: '700' }}>
              {activeTotalOrders} {language === 'ar' ? 'طلب' : 'orders'} · <AedPrice amount={activeTotalRevenue} />
            </span>
          </div>

          {/* 3x2 grid on mobile, 6 cols on desktop */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
            gap: isMobile ? '8px' : '12px'
          }}>
            {statusData.map(s => {
              const cfg = STATUS_CONFIG[s.status];
              const pct = activeTotalOrders > 0 ? Math.round((s.count / activeTotalOrders) * 100) : 0;
              const iconSize = isMobile ? 18 : 24;
              return (
                <div key={s.status} style={{
                  background: s.count > 0 ? cfg.bg : '#fafafa',
                  borderRadius: isMobile ? '16px' : '18px',
                  padding: isMobile ? '12px 10px' : '18px',
                  opacity: s.count === 0 ? 0.4 : 1,
                  border: s.count > 0 ? `1.5px solid ${cfg.border}` : '1.5px solid #f1f5f9',
                  transition: 'all 0.2s'
                }}>
                  {/* Top row: Icon left, % right */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? '8px' : '10px' }}>
                    <StatusIcon status={s.status} color={s.count > 0 ? cfg.color : '#cbd5e1'} size={iconSize} />
                    {s.count > 0 ? (
                      <span style={{ fontSize: isMobile ? '12px' : '15px', fontWeight: '800', color: cfg.color }}>{pct}%</span>
                    ) : (
                      <span style={{ fontSize: isMobile ? '12px' : '15px', fontWeight: '800', color: '#cbd5e1' }}>0%</span>
                    )}
                  </div>
                  {/* Big number */}
                  <div style={{
                    fontSize: isMobile ? '32px' : '42px',
                    fontWeight: '900', color: cfg.color,
                    lineHeight: 1, letterSpacing: '-2px', marginBottom: isMobile ? '4px' : '6px'
                  }}>
                    {s.count}
                  </div>
                  {/* Label */}
                  <div style={{ fontSize: isMobile ? '11px' : '14px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {language === 'ar' ? cfg.label_ar : cfg.label_en}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ====== Product Stats ====== */}
        <div style={{
          background: 'white', borderRadius: '20px', border: '1px solid #e5e7eb',
          padding: isMobile ? '16px' : '24px', marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', marginBottom: isMobile ? '16px' : '20px'
          }}>
            <PackageIcon size={isMobile ? "16px" : "20px"} />
            <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#1e293b' }}>
              {language === 'ar' ? 'إحصائيات المنتجات' : 'Product Stats'}
            </span>
          </div>

          {isMobile ? (
            /* ===== MOBILE: Card layout ===== */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {productStats.map((p, i) => {
                const isExpanded = selectedProduct === p.productId;
                const stockLeft = p.currentStock || 0;
                const base = p.baseStock || 0;
                const sold = Math.max(0, base - stockLeft);
                const shipped = p.shippedQty || 0;
                const notShipped = sold - shipped;
                const soldPct = base > 0 ? Math.round((sold / base) * 100) : 0;
                return (
                <div key={p.productId || i} style={{
                  background: 'white', borderRadius: '20px',
                  border: isExpanded ? '1.5px solid #bfdbfe' : '1px solid #f1f5f9',
                  overflow: 'hidden', opacity: p.enabled === false ? 0.5 : 1
                }}>
                  <div onClick={() => setSelectedProduct(isExpanded ? null : p.productId)} style={{ padding: '16px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                      {p.image && <div style={{ width: '48px', height: '48px', borderRadius: '14px', overflow: 'hidden', flexShrink: 0, background: '#f1f5f9' }}><img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{language === 'ar' && p.nameAr ? p.nameAr : p.name}</div>
                        {language !== 'ar' && p.nameAr ? <div style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nameAr}</div> : null}
                        {p.enabled === false && <span style={{ fontSize: '10px', color: '#ef4444', fontWeight: '700' }}>{language === 'ar' ? 'مخفي' : 'Hidden'}</span>}
                      </div>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, background: isExpanded ? '#3478f6' : '#f1f5f9', color: isExpanded ? 'white' : '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' }}>{isExpanded ? '▲' : '▼'}</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: notShipped > 0 ? '1fr 1fr 1fr 1fr' : (base > 0 ? '1fr 1fr 1fr' : '1fr 1fr'), gap: '6px', marginBottom: base > 0 ? '14px' : 0 }}>
                      <div style={{ background: '#eff6ff', borderRadius: '14px', padding: '12px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '28px', fontWeight: '900', color: '#3478f6', lineHeight: 1, letterSpacing: '-2px' }}>{sold}</div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#3478f6', textTransform: 'uppercase', marginTop: '6px' }}>{language === 'ar' ? 'مباع' : 'Sold'}</div>
                      </div>
                      {notShipped > 0 && <div style={{ background: '#fef3c7', borderRadius: '14px', padding: '12px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '28px', fontWeight: '900', color: '#b45309', lineHeight: 1, letterSpacing: '-2px' }}>{notShipped}</div>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#b45309', textTransform: 'uppercase', marginTop: '6px' }}>{language === 'ar' ? 'لم يشحن' : 'Pending'}</div>
                      </div>}
                      <div style={{ background: stockLeft <= 5 ? '#fef2f2' : '#ecfdf5', borderRadius: '14px', padding: '12px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '28px', fontWeight: '900', color: stockLeft <= 5 ? '#dc2626' : '#059669', lineHeight: 1, letterSpacing: '-2px' }}>{stockLeft}</div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: stockLeft <= 5 ? '#dc2626' : '#059669', textTransform: 'uppercase', marginTop: '6px' }}>{language === 'ar' ? 'متوفر' : 'Available'}</div>
                      </div>
                      {base > 0 && <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '12px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '28px', fontWeight: '900', color: '#64748b', lineHeight: 1, letterSpacing: '-2px' }}>{base}</div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginTop: '6px' }}>{language === 'ar' ? 'الأساسي' : 'Base'}</div>
                      </div>}
                    </div>
                    {base > 0 && <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '800', color: '#64748b' }}>{sold} {language === 'ar' ? 'مباع' : 'sold'}</span>
                        <span style={{ fontSize: '18px', fontWeight: '900', color: soldPct > 80 ? '#dc2626' : soldPct > 50 ? '#d97706' : '#059669' }}>{soldPct}%</span>
                      </div>
                      <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(100, soldPct)}%`, background: soldPct > 80 ? 'linear-gradient(90deg, #ef4444, #dc2626)' : soldPct > 50 ? 'linear-gradient(90deg, #f59e0b, #d97706)' : 'linear-gradient(90deg, #34d399, #059669)', borderRadius: '10px', transition: 'width 0.4s ease' }} />
                      </div>
                    </div>}
                  </div>
                  {isExpanded && (
                    <div style={{ padding: '12px 16px 16px', background: '#f8fafc', borderTop: '1px dashed #e2e8f0' }}>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{language === 'ar' ? 'أكثر المشترين' : 'Top Buyers'}</div>
                      {p.customers && p.customers.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {p.customers.map((c, ci) => (
                            <div key={ci} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'white', borderRadius: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: ci === 0 ? '#3478f6' : '#e5e7eb', color: ci === 0 ? 'white' : '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800' }}>{ci + 1}</span>
                                <div><span style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{c.name || '-'}</span><div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{c.phone}</div></div>
                              </div>
                              <div style={{ fontWeight: '900', color: '#1e293b', fontSize: '16px' }}>×{c.quantity}</div>
                            </div>
                          ))}
                        </div>
                      ) : <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', margin: '8px 0' }}>{language === 'ar' ? 'لم يتم طلب هذا المنتج بعد' : 'No orders yet'}</p>}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          ) : (
            /* ===== DESKTOP: Table layout ===== */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {/* Table header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 2fr',
                gap: '0', padding: '14px 20px',
                background: '#f8fafc', borderRadius: '14px 14px 0 0',
                border: '1px solid #e5e7eb', borderBottom: 'none'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{language === 'ar' ? 'المنتج' : 'Product'}</span>
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#3478f6', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>{language === 'ar' ? 'مباع' : 'Sold'}</span>
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>{language === 'ar' ? 'لم يشحن' : 'Pending'}</span>
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#059669', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>{language === 'ar' ? 'متوفر' : 'Available'}</span>
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>{language === 'ar' ? 'الأساسي' : 'Base'}</span>
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>{language === 'ar' ? 'التقدم' : 'Progress'}</span>
              </div>
              {/* Table rows */}
              {productStats.map((p, i) => {
                const isExpanded = selectedProduct === p.productId;
                const stockLeft = p.currentStock || 0;
                const base = p.baseStock || 0;
                const sold = Math.max(0, base - stockLeft);
                const shipped = p.shippedQty || 0;
                const notShipped = sold - shipped;
                const soldPct = base > 0 ? Math.round((sold / base) * 100) : 0;
                const isLast = i === productStats.length - 1;
                return (
                <div key={p.productId || i}>
                  <div
                    onClick={() => setSelectedProduct(isExpanded ? null : p.productId)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 2fr',
                      gap: '0', alignItems: 'center',
                      padding: '18px 20px',
                      background: isExpanded ? '#f0f7ff' : (i % 2 === 0 ? 'white' : '#fafbfc'),
                      border: '1px solid #e5e7eb', borderTop: 'none',
                      borderRadius: isLast && !isExpanded ? '0 0 14px 14px' : '0',
                      cursor: 'pointer', transition: 'background 0.15s',
                      opacity: p.enabled === false ? 0.5 : 1
                    }}
                  >
                    {/* Product name + image */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                      {p.image && <div style={{ width: '52px', height: '52px', borderRadius: '14px', overflow: 'hidden', flexShrink: 0, background: '#f1f5f9' }}><img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: '700', fontSize: '17px', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {language === 'ar' && p.nameAr ? p.nameAr : p.name}
                          {p.enabled === false && <span style={{ fontSize: '14px', color: '#ef4444', marginLeft: '8px' }}>{language === 'ar' ? 'مخفي' : 'Hidden'}</span>}
                        </div>
                        {language !== 'ar' && p.nameAr ? <div style={{ fontSize: '14px', color: '#9ca3af' }}>{p.nameAr}</div> : null}
                      </div>
                    </div>
                    {/* Sold */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '36px', fontWeight: '900', color: '#3478f6', lineHeight: 1, letterSpacing: '-2px' }}>{sold}</div>
                    </div>
                    {/* Pending */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '36px', fontWeight: '900', color: notShipped > 0 ? '#b45309' : '#e2e8f0', lineHeight: 1, letterSpacing: '-2px' }}>{notShipped}</div>
                    </div>
                    {/* Available */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '36px', fontWeight: '900', color: stockLeft <= 5 ? '#dc2626' : '#059669', lineHeight: 1, letterSpacing: '-2px' }}>{stockLeft}</div>
                    </div>
                    {/* Base */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '36px', fontWeight: '900', color: '#64748b', lineHeight: 1, letterSpacing: '-2px' }}>{base}</div>
                    </div>
                    {/* Progress */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ height: '10px', background: '#e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(100, soldPct)}%`, background: soldPct > 80 ? 'linear-gradient(90deg, #ef4444, #dc2626)' : soldPct > 50 ? 'linear-gradient(90deg, #f59e0b, #d97706)' : 'linear-gradient(90deg, #34d399, #059669)', borderRadius: '10px', transition: 'width 0.4s ease' }} />
                        </div>
                      </div>
                      <span style={{ fontSize: '21px', fontWeight: '900', color: soldPct > 80 ? '#dc2626' : soldPct > 50 ? '#d97706' : '#059669', minWidth: '50px', textAlign: 'right', letterSpacing: '-0.5px' }}>{soldPct}%</span>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, background: isExpanded ? '#3478f6' : '#f1f5f9', color: isExpanded ? 'white' : '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', transition: 'all 0.2s' }}>{isExpanded ? '▲' : '▼'}</div>
                    </div>
                  </div>
                  {/* Expanded buyers */}
                  {isExpanded && (
                    <div style={{ padding: '16px 24px 20px', background: '#f8fafc', border: '1px solid #e5e7eb', borderTop: '1px dashed #e2e8f0', borderRadius: isLast ? '0 0 14px 14px' : '0' }}>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#64748b', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{language === 'ar' ? 'أكثر المشترين' : 'Top Buyers'}</div>
                      {p.customers && p.customers.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {p.customers.map((c, ci) => (
                            <div key={ci} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'white', borderRadius: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: ci === 0 ? '#3478f6' : '#e5e7eb', color: ci === 0 ? 'white' : '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '800' }}>{ci + 1}</span>
                                <div><span style={{ fontWeight: '700', color: '#1e293b', fontSize: '15px' }}>{c.name || '-'}</span><div style={{ fontSize: '14px', color: '#9ca3af', fontFamily: 'monospace' }}>{c.phone}</div></div>
                              </div>
                              <div style={{ fontWeight: '900', color: '#1e293b', fontSize: '20px' }}>×{c.quantity}</div>
                            </div>
                          ))}
                        </div>
                      ) : <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', margin: '8px 0' }}>{language === 'ar' ? 'لم يتم طلب هذا المنتج بعد' : 'No orders yet'}</p>}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ====== Top Customers ====== */}
        <div style={{
          background: 'white', borderRadius: '20px', border: '1px solid #e5e7eb',
          padding: isMobile ? '16px' : '24px'
        }}>
          <div
            onClick={() => setShowCustomers(!showCustomers)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer', marginBottom: showCustomers ? (isMobile ? '16px' : '20px') : 0
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UsersIcon size={isMobile ? "16px" : "20px"} />
              <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#1e293b' }}>
                {language === 'ar' ? 'أكثر العملاء طلباً' : 'Top Customers'}
              </span>
              <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: '#94a3b8' }}>({topCustomers.length})</span>
            </div>
            <div style={{
              width: isMobile ? '28px' : '32px', height: isMobile ? '28px' : '32px', borderRadius: '50%',
              background: showCustomers ? '#3478f6' : '#f1f5f9',
              color: showCustomers ? 'white' : '#9ca3af',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isMobile ? '11px' : '14px', transition: 'all 0.2s'
            }}>
              {showCustomers ? '▲' : '▼'}
            </div>
          </div>

          {!showCustomers ? null : topCustomers.length === 0 ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px', fontSize: '14px' }}>
              {language === 'ar' ? 'لا توجد بيانات' : 'No data yet'}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {topCustomers.map((c, i) => (
                <div key={i} style={{
                  background: i === 0 ? '#fefce8' : '#fafafa',
                  borderRadius: '14px',
                  border: i === 0 ? '1.5px solid #fde68a' : '1px solid #f1f5f9',
                  overflow: 'hidden'
                }}>
                  <div
                    onClick={() => setExpandedCustomer(expandedCustomer === i ? null : i)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: isMobile ? '12px' : '14px 18px', cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '12px', minWidth: 0 }}>
                      <span style={{
                        width: isMobile ? '26px' : '32px', height: isMobile ? '26px' : '32px', borderRadius: '50%',
                        background: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#cd7c2f' : '#e5e7eb',
                        color: i < 3 ? 'white' : '#6b7280',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: isMobile ? '11px' : '13px', fontWeight: '800', flexShrink: 0
                      }}>{i + 1}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: '700', fontSize: isMobile ? '13px' : '15px', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.name || (language === 'ar' ? 'بدون اسم' : 'No name')}
                        </div>
                        <div style={{ fontSize: isMobile ? '11px' : '13px', color: '#9ca3af', fontFamily: 'monospace' }}>{c.phone}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: '800', fontSize: isMobile ? '16px' : '20px', color: '#1e293b' }}>{c.orderCount}</div>
                        <div style={{ fontSize: isMobile ? '11px' : '14px', color: '#9ca3af' }}><AedPrice amount={c.totalSpent} /></div>
                      </div>
                      <div style={{
                        width: isMobile ? '24px' : '28px', height: isMobile ? '24px' : '28px', borderRadius: '50%', flexShrink: 0,
                        background: expandedCustomer === i ? '#3478f6' : '#e5e7eb',
                        color: expandedCustomer === i ? 'white' : '#9ca3af',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: isMobile ? '10px' : '14px', transition: 'all 0.2s'
                      }}>
                        {expandedCustomer === i ? '▲' : '▼'}
                      </div>
                    </div>
                  </div>

                  {/* Expanded orders */}
                  {expandedCustomer === i && c.orders && (
                    <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {c.orders.map(order => (
                        <Link
                          key={order.id}
                          to={`/dashboard4/orders/${order.id}`}
                          style={{
                            display: 'block', padding: '10px 12px',
                            background: 'white', borderRadius: '10px',
                            border: '1px solid #e5e7eb', textDecoration: 'none', color: 'inherit'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '700', color: '#3478f6' }}>{order.id}</span>
                            <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                              {new Date(order.createdAt).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-AE', { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                          {order.items.map((item, ii) => (
                            <div key={ii} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#4b5563' }}>
                              <span>{language === 'ar' && item.nameAr ? item.nameAr : item.name} × {item.quantity}</span>
                              <span style={{ fontWeight: '600' }}><AedPrice amount={item.price * item.quantity} /></span>
                            </div>
                          ))}
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '4px', borderTop: '1px dashed #e5e7eb', fontSize: '14px', fontWeight: '800' }}>
                            <span>{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
                            <span style={{ color: '#1e293b' }}>
                              <AedPrice amount={order.total} />
                              {order.payToDriver ? <span style={{ fontSize: isMobile ? '10px' : '14px', color: '#b45309', marginLeft: '4px' }}>🚚</span> : ''}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Order ID pills when collapsed */}
                  {expandedCustomer !== i && c.orderIds && c.orderIds.length > 0 && (
                    <div style={{ padding: '0 12px 10px 48px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {c.orderIds.map(oid => (
                        <Link
                          key={oid}
                          to={`/dashboard4/orders/${oid}`}
                          style={{
                            fontSize: isMobile ? '10px' : '14px', fontFamily: 'monospace',
                            padding: isMobile ? '2px 8px' : '4px 10px', borderRadius: '6px',
                            background: i === 0 ? '#fef9c3' : '#eff6ff',
                            color: '#3478f6', textDecoration: 'none',
                            border: '1px solid', borderColor: i === 0 ? '#fde68a' : '#dbeafe',
                            fontWeight: '600'
                          }}
                        >{oid}</Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Stats;
